import { useEffect } from 'react';
import { useBeforeUnload } from 'react-router-dom';

interface UseUnsavedChangesGuardOptions {
  when: boolean;
  message: string;
}

function shouldIgnoreClick(event: MouseEvent): boolean {
  return event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

function isExternalNavigation(anchor: HTMLAnchorElement, currentUrl: URL): boolean {
  return new URL(anchor.href, currentUrl.href).origin !== currentUrl.origin;
}

function isSameDocumentNavigation(anchor: HTMLAnchorElement, currentUrl: URL): boolean {
  const nextUrl = new URL(anchor.href, currentUrl.href);

  return (
    nextUrl.origin === currentUrl.origin &&
    nextUrl.pathname === currentUrl.pathname &&
    nextUrl.search === currentUrl.search &&
    nextUrl.hash === currentUrl.hash
  );
}

export function useUnsavedChangesGuard({ when, message }: UseUnsavedChangesGuardOptions): void {
  useBeforeUnload(
    (event) => {
      if (!when) {
        return;
      }

      event.preventDefault();
      event.returnValue = message;
    },
    { capture: true },
  );

  useEffect(() => {
    if (!when || typeof window === 'undefined') {
      return undefined;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (shouldIgnoreClick(event)) {
        return;
      }

      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest('a[href]');

      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }

      if (anchor.target && anchor.target !== '_self') {
        return;
      }

      if (anchor.hasAttribute('download')) {
        return;
      }

      const href = anchor.getAttribute('href');

      if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
        return;
      }

      const currentUrl = new URL(window.location.href);

      if (isSameDocumentNavigation(anchor, currentUrl)) {
        return;
      }

      if (isExternalNavigation(anchor, currentUrl)) {
        if (!window.confirm(message)) {
          event.preventDefault();
          event.stopPropagation();
        }

        return;
      }

      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [message, when]);
}
